import React, { memo, useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { hasRole } from "../utils/roles";
// Nav data + visibility rules live in utils/nav so they can be unit-tested without React.
import { NavItem, PRIMARY, ADDITIONAL_RESOURCES, ACCOUNT, filterItems } from "../utils/nav";
// Shared with DashboardHome so the nav and the landing page agree on what a pending caller sees.
import { readReqStatus } from "../utils/reqStatus";
import AccountSwitcher from "./AccountSwitcher";

const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/assets/images/logo.png`;
/**
 * The mark on its own, for the collapsed rail.
 *
 * The rail used to show the full wordmark scaled to `cover` a 35x35 box and pinned to its left
 * edge — i.e. it cropped a square out of the lockup and hoped the mark was what landed in it. That
 * silently depends on the ratio between the mark and the word next to it, so a new logo re-cuts
 * the icon: this one is 760x176 rather than 192x64, and the crop caught the mark plus a slice of
 * the gap after it, flush against the left edge. A square asset just is the mark, at any size.
 */
const LOGO_MARK_SRC = `${process.env.PUBLIC_URL || ""}/assets/images/logo-mark.png`;

// ACOP-119: shown only to an active support-admin (hasRole(roles, "support") — see determineRoles
// in copilot-api, which only grants it once Status__c === "Approved"), in both nav states. Still
// deliberately rendered as a sibling of the footer div below rather than nested in it — that div is
// gated behind `(canToggleBetaPreview || !forcedCollapsed)`, which is false (nothing renders) for a
// non-employee on a narrow/forced-collapse viewport, and support-admins aren't necessarily employees.
const SUPPORT_PHONE_DISPLAY = "844-894-8051";
const SUPPORT_PHONE_TEL = "tel:+18448948051";
const SUPPORT_PHONE_TITLE = `Support: ${SUPPORT_PHONE_DISPLAY}, press 3 for Support, or ext. 231`;

function dismissCopilotOffcanvas() {
  const el = document.getElementById("copilotSideNav");
  if (el && typeof window !== "undefined" && window.bootstrap?.Offcanvas) {
    const inst = window.bootstrap.Offcanvas.getInstance(el);
    if (inst) inst.hide();
  }
}

function pathIsActive(pathname: string, itemPath: string) {
  if (itemPath === "/") return pathname === "/";
  if (itemPath === "/copilot")
    return pathname === "/copilot" || pathname.startsWith("/copilot/");
  if (itemPath === "/resources/knowledge-base")
    return pathname.startsWith("/resources/knowledge-base");
  if (itemPath === "/resources/university")
    return pathname.startsWith("/resources/university");
  if (itemPath === "/resources/embed")
    return pathname.startsWith("/resources/embed");
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function profileLabel(roles: unknown[], reqStatus: string) {
  if (reqStatus === "Requested") return "My pending profile";
  if (hasRole(roles, "support")) return "My admin profile";
  return "My profile";
}

type Props = {
  roles: unknown[];
  /** Accounts this caller may act as + the active one (ACOP-79); drives the account switcher. */
  accountCtx?: { accounts: { id: string; name: string }[]; selectedAccountId: string };
  collapsed: boolean;
  /** True when the viewport forces collapsed mode; user toggle is hidden here. */
  forcedCollapsed?: boolean;
  onToggleCollapsed: () => void;
  /** Bust memo + resync reqData-derived state when the session becomes active after login. */
  sessionActive: boolean;
  /** Resolved Beta Preview state — already role-checked upstream (utils/betaPreview.ts). */
  betaPreviewOn: boolean;
  /** Whether to offer the switch at all. Employees only (ACOP-140): false for everyone else, and for a denied or unresolved caller. */
  canToggleBetaPreview: boolean;
  onToggleBetaPreview: () => void;
};

function DashboardSideNav({
  roles,
  accountCtx,
  collapsed,
  forcedCollapsed = false,
  onToggleCollapsed,
  sessionActive,
  betaPreviewOn,
  canToggleBetaPreview,
  onToggleBetaPreview,
}: Props) {
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = navRef.current;
    if (!root || typeof window === "undefined" || !window.bootstrap?.Tooltip) {
      return;
    }
    const triggers = root.querySelectorAll<HTMLElement>(
      '[data-bs-toggle="tooltip"]',
    );
    const instances: any[] = [];
    triggers.forEach((el) => {
      const existing = window.bootstrap.Tooltip.getInstance(el);
      if (existing) existing.dispose();
      instances.push(
        new window.bootstrap.Tooltip(el, {
          container: "body",
          placement: "right",
          trigger: "hover focus",
          delay: { show: 50, hide: 0 },
        }),
      );
    });
    return () => {
      instances.forEach((t) => {
        try {
          t.dispose();
        } catch {
          /* noop */
        }
      });
    };
  }, [collapsed, roles, sessionActive, location.pathname]);
  /* Re-read on the same triggers as before this moved into a helper: a navigation, the session
     going active, or a role refresh. They are cues rather than inputs — the value comes from
     storage, not from props — which is exactly what exhaustive-deps objects to. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reqStatus = useMemo(() => readReqStatus(), [location.pathname, sessionActive, roles]);

  const itemDisplayLabel = (item: NavItem) =>
    item.path === "/profile" ? profileLabel(roles, reqStatus) : item.label;

  const renderSection = (title: string, items: NavItem[]) => {
    if (!items.length) return null;
    return (
    <div className="copilot-sidenav-section">
      <div
        className="copilot-sidenav-section-heading text-uppercase text-muted"
        aria-hidden={collapsed}
      >
        {title}
      </div>
      <ul className="nav flex-column copilot-sidenav-nav-list">
        {items.map((item) => {
          const label = itemDisplayLabel(item);
          const active = pathIsActive(location.pathname, item.path);
          return (
            <li className="nav-item" key={item.path}>
              <NavLink
                to={item.path}
                end={item.path === "/"}
                onClick={() => dismissCopilotOffcanvas()}
                className={`nav-link text-start w-100 d-flex align-items-center copilot-sidenav-link text-decoration-none ${
                  active ? "active" : ""
                } ${collapsed ? "copilot-sidenav-link--icon-only justify-content-center" : ""}`}
                data-testid="sidenav-link"
                data-id={item.path}
                aria-current={active ? "page" : undefined}
                aria-label={collapsed ? label : undefined}
                title={collapsed ? label : ""}
                data-bs-toggle={collapsed ? "tooltip" : undefined}
                data-bs-placement="right"
                data-bs-title={collapsed ? label : undefined}
              >
                <i
                  className={`${item.icon} copilot-sidenav-icon`}
                  aria-hidden
                />
                <span className="copilot-sidenav-link-text">{label}</span>
                {/* Beta mark, for any entry flagged `beta` (only Training, the employee-only LMS beta, since ACOP-140).
                    A flask rather than the word "(beta)", which would lengthen every entry.
                    margin-left:auto pushes it to the right edge; in the
                    collapsed rail the CSS re-pins it as a small badge on the icon, since there is
                    no row left to right-justify within.

                    aria-hidden on the icon with a visually-hidden word beside it, so the badge is
                    a picture to sighted users and still reads as "beta" to a screen reader — it
                    carries meaning, so it must not be decorative-only. */}
                {item.beta && (
                  <>
                    <i className="fa-solid fa-flask copilot-sidenav-beta-mark" aria-hidden />
                    <span className="visually-hidden">beta</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
    );
  };

  const asideClass = `offcanvas offcanvas-start copilot-sidenav copilot-sidenav--modern${
    collapsed ? " copilot-sidenav--collapsed" : ""
  }`;

  return (
    <aside
      ref={navRef}
      className={asideClass}
      tabIndex={-1}
      id="copilotSideNav"
      aria-labelledby="copilotSideNavLabel"
    >
      <h2 id="copilotSideNavLabel" className="visually-hidden">Menu</h2>
      <div className="offcanvas-body copilot-sidenav-panel d-flex flex-column p-0">
        <div className="copilot-sidenav-brand d-flex align-items-center">
          <div className="d-flex align-items-center min-w-0 copilot-sidenav-brand-row w-100">
            <img
              src={collapsed ? LOGO_MARK_SRC : LOGO_SRC}
              alt="AvSight"
              className={`copilot-sidenav-logo-img flex-shrink-0 ${collapsed ? "copilot-sidenav-logo-img--collapsed" : ""}`}
            />
          </div>
        </div>

        {/* Multi-account callers pick their active account here; renders nothing for everyone else. */}
        <AccountSwitcher collapsed={collapsed} accountCtx={accountCtx} />

        <nav
          className="flex-grow-1 copilot-sidenav-nav overflow-y-auto"
          aria-label="Main navigation"
        >
          {/* Learning: Home, Copilot and the Knowledge Base are the live experience for everyone
              (ACOP-140). Beta Preview swaps the other two (ACOP-163): ON lists Training and hides
              AvSight University, OFF the reverse. `betaPreviewOn` is a prop, so flipping the switch
              re-renders the menu at once with no reload; filterItems applies the per-item gates. */}
          {renderSection("Learning", filterItems(PRIMARY, roles, reqStatus, betaPreviewOn))}
          {renderSection("Product Changes", filterItems(ADDITIONAL_RESOURCES, roles, reqStatus, betaPreviewOn))}
          {renderSection("Support", filterItems(ACCOUNT, roles, reqStatus, betaPreviewOn))}
        </nav>

        {hasRole(roles, "support") && (
          <div
            className={`copilot-sidenav-support d-flex align-items-center mt-auto mb-2 ${
              collapsed ? "justify-content-center" : "gap-2 copilot-sidenav-support--expanded"
            }`}
          >
            <a
              href={SUPPORT_PHONE_TEL}
              className="copilot-sidenav-support-link d-flex align-items-center text-decoration-none"
              title={SUPPORT_PHONE_TITLE}
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              data-bs-title={SUPPORT_PHONE_TITLE}
              data-testid="sidenav-support-phone"
            >
              <i className="fa-solid fa-phone copilot-sidenav-support-icon" aria-hidden />
              {!collapsed && (
                <span className="copilot-sidenav-support-text ms-2">
                  <span className="copilot-sidenav-support-label">Contact Support</span>
                  <span className="copilot-sidenav-support-number">{SUPPORT_PHONE_DISPLAY}</span>
                </span>
              )}
              <span className="visually-hidden">{SUPPORT_PHONE_TITLE}</span>
            </a>
          </div>
        )}

        {(canToggleBetaPreview || !forcedCollapsed) && (
          <div className="copilot-sidenav-footer">
            {/* Beta Preview — employees only (ACOP-140), and deliberately ABOVE the collapse
                control so the switch sits with the other footer controls.

                Kept outside the !forcedCollapsed guard that hides the collapse button: that button
                is hidden on a narrow viewport because the viewport already forces collapse, which
                is no reason to take the beta switch away from a user on a phone.

                A real checkbox rather than a styled div, so it is keyboard-reachable and announces
                its own state; the visual switch is CSS on top of it. */}
            {canToggleBetaPreview && (
              <label
                className={`copilot-sidenav-beta d-flex align-items-center mb-2 ${
                  collapsed ? "copilot-sidenav-beta--icon-only justify-content-center" : "gap-2"
                }`}
                title={collapsed ? `Beta Preview: ${betaPreviewOn ? "on" : "off"}` : "Preview mode: adds the other knowledge sources and the Training beta (employees only)"}
              >
                <input
                  type="checkbox"
                  className="form-check-input copilot-sidenav-beta-input m-0 flex-shrink-0"
                  checked={betaPreviewOn}
                  onChange={onToggleBetaPreview}
                  data-testid="beta-preview-toggle"
                  aria-label="Beta Preview"
                />
                <span className="copilot-sidenav-footer-btn-text copilot-sidenav-beta-text">
                  Beta Preview
                </span>
                {/* The same flask a beta nav entry carries and the beta sources in the Copilot picker
                    carry, so the switch is visibly the control for them. Same class, so it
                    right-aligns and inherits the hide-in-collapsed-rail rule. */}
                <i className="fa-solid fa-flask copilot-sidenav-beta-mark" aria-hidden />
              </label>
            )}
            {!forcedCollapsed && (
            <button
              type="button"
              className={`btn copilot-sidenav-footer-toggle w-100 d-flex align-items-center copilot-sidenav-footer-btn mb-2 ${
                collapsed
                  ? "copilot-sidenav-footer-btn--icon-only justify-content-center"
                  : "justify-content-start gap-2"
              }`}
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-controls="copilotSideNav"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <i className="fa-solid fa-bars" aria-hidden />
              <span className="copilot-sidenav-footer-btn-text">
                {collapsed ? "Full" : "Icons Only"}
              </span>
            </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

export default memo(DashboardSideNav);
